/**
 * NationalEducationKs2Page — the KS2 國民教育題庫 sample-question page.
 *
 * WHAT CHANGED (2026-11-03)
 * -------------------------
 * Like its sibling (NationalEducationPage), this used to be a hand-coded
 * standalone document: `public/products/national-education/KS2-v3.html`,
 * mirrored into the three locale directories by `mirrorStandalone()` and
 * served by nginx with no site chrome. It is now a React route under
 * `PublicLayout`, so it gains the YADA header bar and footer like every
 * other page on the site.
 *
 * THE URL STAYS A `.html` FILE
 * ----------------------------
 * `…/national-education/KS2-v3.html` is a FILE, not a directory: nginx
 * serves it at exactly that path and `…/KS2-v3.html/` is a 404. So its
 * canonical carries NO trailing slash, and `buildCanonicalUrl()` has a
 * matching `.html` exception (see src/utils/siteMeta.ts). The hreflang
 * siblings are emitted by the same helper, so all three locales agree.
 *
 * THE CONTENT IS BILINGUAL, AND THE LOCALE PICKS THE SIDE
 * --------------------------------------------------------
 * The standalone document wrote every string twice — a `.lang-zh` span and
 * a `.lang-en` span — and CSS showed whichever the document's language
 * attribute asked for. In React that indirection is unnecessary and
 * actively harmful: shipping both strings would put the Chinese text in
 * the English page's DOM, where a crawler would read it as duplicate
 * content on a page that claims to be English. So the copy now lives in
 * the locale files and the page renders ONE language, chosen by the
 * locale the URL is in.
 *
 * The stylesheet is imported from src/main.tsx, NOT from here — the
 * prerender pass renders this component in plain Node via `tsx`, and Node
 * cannot import a `.css` file.
 */
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { buildCanonicalUrl, SITE_NAME, SITE_URL } from '@/utils/siteMeta'
import SEO from '@/utils/seo'
import { buildBreadcrumbList } from '@/utils/structuredData'
import { useSlotT, useSlotObject } from '@/cms/useSlotT'

interface StatItem {
  num: string
  lbl: string
}

interface UnitItem {
  num: string
  name: string
}

interface OptionItem {
  key: string
  text: string
  correct?: boolean
}

interface QuestionItem {
  no: string
  text: string
  options: OptionItem[]
}

interface GroupItem {
  tag: string
  title: string
  questions: QuestionItem[]
}

export default function NationalEducationKs2Page() {
  const { t } = useTranslation()
  const { pathname } = useLocation()

  // CMS slot hooks — use ambient locale (KS2 is fully trilingual)
  const slotT = useSlotT('national-education-ks2')
  const slotObj = useSlotObject('national-education-ks2')

  const seo = {
    title: slotT('seo.nationalEducationKs2Title'),
    description: slotT('seo.nationalEducationKs2Description'),
    keywords: slotT('seo.nationalEducationKs2Keywords'),
  }

  const hero = slotObj('nationalEducation.ks2.hero') as {
    kicker: string
    title: string
    sub: string
    stats: StatItem[]
  }

  const unitsSection = slotObj('nationalEducation.ks2.unitsSection') as { title: string; sub: string }

  const units = slotObj('nationalEducation.ks2.units') as UnitItem[]

  const samplesSection = slotObj('nationalEducation.ks2.samplesSection') as { title: string; sub: string }

  const groups = slotObj('nationalEducation.ks2.groups') as GroupItem[]

  const correctAnswer = slotT('nationalEducation.ks2.correctAnswer')

  const breadcrumb = slotObj('nationalEducation.ks2.breadcrumb') as {
    home: string
    products: string
    parent: string
    self: string
  }

  const canonicalUrl = buildCanonicalUrl(pathname)

  return (
    <>
      <SEO
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: seo.title,
            description: seo.description,
            url: canonicalUrl,
            inLanguage: ['zh-Hant', 'zh-Hans', 'en'],
            publisher: {
              '@type': 'Organization',
              name: SITE_NAME,
              url: `${SITE_URL}/`,
            },
          },
          buildBreadcrumbList([
            { name: breadcrumb.home, path: '/' },
            { name: breadcrumb.products, path: '/products' },
            { name: breadcrumb.parent, path: '/products/national-education' },
            {
              name: breadcrumb.self,
              path: '/products/national-education/KS2-v3.html',
            },
          ]),
        ]}
      />

      <div className="ne2-root">
        <header className="hero">
          <div className="kicker">{hero.kicker}</div>
          <h1>{hero.title}</h1>
          <p>{hero.sub}</p>
          <div className="stats">
            {hero.stats.map((stat) => (
              <div className="stat" key={stat.lbl}>
                <div className="num">{stat.num}</div>
                <div className="lbl">{stat.lbl}</div>
              </div>
            ))}
          </div>
        </header>

        <div className="container">
          {/* ── The 12 units ── */}
          <section className="section">
            <div className="ne2-section-title">{unitsSection.title}</div>
            <div className="section-sub">{unitsSection.sub}</div>

            <div className="unit-grid">
              {units.map((unit) => (
                <div className="unit-card" key={unit.num}>
                  <div className="num">{unit.num}</div>
                  <div className="name">{unit.name}</div>
                </div>
              ))}
            </div>
          </section>

          {/* ── The 24 sample questions ── */}
          <section className="section">
            <div className="ne2-section-title">{samplesSection.title}</div>
            <div className="section-sub">{samplesSection.sub}</div>

            {groups.map((group) => (
              <div className="sample-group" key={group.tag}>
                <div className="sample-group-head">
                  <span className="tag">{group.tag}</span>
                  <span className="title">{group.title}</span>
                </div>
                <div className="sample-body">
                  {group.questions.map((question) => (
                    <div className="q-card" key={`${group.tag}-${question.no}`}>
                      <div className="q-text">
                        <span className="qno">{question.no}</span>
                        {question.text}
                      </div>
                      <ul className="q-opts">
                        {question.options.map((option) => (
                          <li className={option.correct ? 'correct' : undefined} key={option.key}>
                            <span className="key">{option.key}</span>
                            {option.text}
                            {option.correct && (
                              <span className="sr-only">{correctAnswer}</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </section>

          {/* Back to Products button */}
          <div className="back-to-products text-center mt-12">
            <Link
              to="/products"
              className="inline-flex items-center gap-2 text-ink-soft hover:text-primary transition-colors font-medium focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2"
            >
              <Icon icon="lucide:arrow-left" className="text-base" aria-hidden="true" />
              <span>{t('products.backToProducts')}</span>
            </Link>
          </div>
        </div>
      </div>
    </>
  )
}
